import { Box, Typography } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import React, { useId, useMemo } from "react";

import {
  OrganizationRole,
  ProductOrganizationGroup,
} from "../../types/productPlane";
import { usePrinting } from "../../utils/export/ExportStage";
import { CountryFlag } from "../common/CountryFlag";
import DerivedTag from "../Detail/DerivedTag";
import EntityLink from "../Detail/EntityLink";
import SourcesChip from "../Detail/SourcesChip";
import Tag from "../Detail/Tag";
import { codeWords } from "../Detail/format";
import ProductRelationTable from "./ProductRelationTable";
import { MakerEntry, makerEntries, organizationRows } from "./productRelations";

interface ProductOrganizationsProps {
  // The organizations around the product, by role
  groups: ProductOrganizationGroup[];
}

// The number of organization relations in the groups
export const countOrganizations = (
  groups: ProductOrganizationGroup[],
): number => groups.reduce((count, group) => count + group.items.length, 0);

// The part of a maker on its card
const MAKER_ROLE_WORDS: Partial<Record<OrganizationRole, string>> = {
  maker: "manufacturer",
  developer: "developer",
};

const subheading = {
  color: "text.secondary",
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

// The kinds of organization a card names in its footer: those its role
// tags do not already say ("manufacturer" twice says nothing)
const footerTypes = (entry: MakerEntry): string[] => {
  const said = new Set(entry.roles.map((role) => MAKER_ROLE_WORDS[role] ?? codeWords(role)));
  return entry.types.map(codeWords).filter((type) => !said.has(type));
};

// The kind of organization in the footer: a plain tag, apart from the
// coloured tags of its parts; the first kind, the others counted and named
// on hover
const TypeChip: React.FC<{ types: string[] }> = ({ types }) => (
  <Box sx={{ minWidth: 0, overflow: "hidden" }}>
    <Tag title={types.length > 1 ? types.join(", ") : undefined}>
      {types[0]}
      {types.length > 1 && ` +${types.length - 1}`}
    </Tag>
  </Box>
);

// The colour of a maker's part on its tag; none for a part it has no
// colour for
const roleHue = (theme: Theme, role: OrganizationRole): string | undefined =>
  role === "maker" || role === "developer" ? theme.palette.chart.makerRoles[role] : undefined;

// One maker as a card: the flag and the name large; beneath, so that the
// feet of a row of cards line up whatever the length of the names, its
// parts and the mark of a derivation as quiet tags, free to wrap; under a
// hairline, a footer of one line: the kind of organization, and its
// sources as one chip that lists them
const MakerCard: React.FC<{ entry: MakerEntry }> = ({ entry }) => {
  const theme = useTheme();
  const types = footerTypes(entry);
  return (
    <Box
      component="li"
      aria-label={entry.organization.name ?? undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
        px: 1.75,
        py: 1.5,
        backgroundColor: "background.raised",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
      }}
    >
      {/* The flag sits on the first line of a name that wraps */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {entry.country && (
          <CountryFlag
            code={entry.country}
            size={16}
            round
            sx={{ flexShrink: 0, mt: "4px" }}
          />
        )}
        <EntityLink
          id={entry.organization.id}
          type={entry.organization.type}
          name={entry.organization.name}
          sx={{ fontSize: "1rem", fontWeight: 500, lineHeight: 1.5 }}
        />
      </Box>
      <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75 }}>
          {entry.roles.map((role) => (
            <Tag key={role} hue={roleHue(theme, role)}>
              {MAKER_ROLE_WORDS[role] ?? codeWords(role)}
            </Tag>
          ))}
          {entry.derived && (
            <DerivedTag basis={entry.derived.basis} variant="chip" />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            pt: 1.25,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          {types.length > 0 && <TypeChip types={types} />}
          <Box sx={{ ml: "auto", flexShrink: 0 }}>
            <SourcesChip sources={entry.sources} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * The organizations around a product: the makers first, one card each,
 * then the other roles as a relation table, role by role in a fixed order,
 * each organization with the kinds its record names.
 */
const ProductOrganizations: React.FC<ProductOrganizationsProps> = ({
  groups,
}) => {
  const makers = useMemo(() => makerEntries(groups), [groups]);
  const others = useMemo(() => organizationRows(groups), [groups]);
  const makersHeading = useId();
  const print = usePrinting();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {makers.length > 0 && (
        <Box>
          <Typography id={makersHeading} component="h3" sx={{ ...subheading, mb: 1 }}>
            Makers
          </Typography>
          <Box
            component="ul"
            aria-labelledby={makersHeading}
            sx={{
              listStyle: "none",
              m: 0,
              p: 0,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: 1.25,
            }}
          >
            {makers.map((entry) => (
              <MakerCard key={entry.organization.id} entry={entry} />
            ))}
          </Box>
        </Box>
      )}

      {others.length > 0 && (
        <Box>
          {makers.length > 0 && (
            <Typography component="h3" sx={{ ...subheading, mb: 1 }}>
              Other roles
            </Typography>
          )}
          <ProductRelationTable
            rows={others}
            entityHeading="Organization"
            columns={["countries", "quantity", "latest"]}
            showRelation
            print={print}
          />
        </Box>
      )}
    </Box>
  );
};

export default ProductOrganizations;
